import { stripHtml } from '~/ui-rocco/pages/detail-shared/utils';

/** A block of markup as paragraphs of plain text. */
export const paragraphsOf = (html: string | null | undefined): string[] =>
    (html ?? '')
        .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .split('\n')
        .map((line) => stripHtml(line).replace(/\s+/g, ' ').trim())
        .filter(Boolean);

const SENTENCE = /[^.!?]+[.!?]+["')\]]*\s*|[^.!?]+$/g;

/**
 * How much of the introduction the opening lead may take.
 *
 * Shared rather than set per caller: the Downloads tile words its cover as the
 * PDF words its own, so a lead cut to a different length on either side would
 * show the reader one opening on the tile and another in the book.
 */
export const PROFILE_LEAD_MAX_CHARS = 200;

/**
 * The introduction's opening in whole sentences, as many as fit the lead's
 * allowance and always the first; the rest stays with the essay.
 */
export const splitLead = (
    paragraphs: string[],
    maxChars: number,
): { lead: string; rest: string[] } => {
    const [first = '', ...others] = paragraphs;
    const sentences = first.match(SENTENCE) ?? [];
    let lead = '';
    let used = 0;
    for (const sentence of sentences) {
        if (lead && lead.length + sentence.length > maxChars) break;
        lead += sentence;
        used += 1;
    }
    const remainder = sentences.slice(used).join('').trim();
    return {
        lead: lead.trim(),
        rest: remainder ? [remainder, ...others] : others,
    };
};

export const plainText = (html: string | null | undefined): string =>
    stripHtml(html ?? '')
        .replace(/\s+/g, ' ')
        .trim();

/** A website as print sets it: the domain, without scheme, path or tracking. */
export const bareWebsite = (url: string | null | undefined): string => {
    const value = (url ?? '').trim();
    if (!value) return '';
    try {
        const parsed = new URL(
            /^https?:/i.test(value) ? value : `https://${value}`,
        );
        return parsed.hostname.replace(/^www\./i, '');
    } catch {
        return value.replace(/^https?:\/\//i, '').split(/[/?#]/)[0] ?? '';
    }
};

/**
 * A download's title as a reader would write it: the file name decoded, its
 * query and extension dropped, and slug dashes and underscores as spaces. A
 * name that is mostly digits, a SKU rather than a title, gives way to the
 * fallback.
 */
export const downloadTitle = (title: string, fallback: string): string => {
    let value = title.split('?')[0] ?? '';
    try {
        value = decodeURIComponent(value);
    } catch {
        // A stray percent sign: keep the name as written.
    }
    value = value.replace(/\.[a-z0-9]{2,4}$/i, '').replace(/_+/g, ' ');
    if (!/\s/.test(value)) value = value.replace(/-+/g, ' ');
    value = value.replace(/\s+/g, ' ').trim();
    const letters = value.replace(/[^a-z]/gi, '').length;
    const digits = value.replace(/\D/g, '').length;
    if (letters === 0 || digits > letters) return fallback;
    return value.charAt(0).toUpperCase() + value.slice(1);
};

const SHORT_WORD = 3;

/**
 * A name set in title case where it was typed in capitals or lower case,
 * keeping short capitalised words such as "LED" as they are.
 */
export const titleCaseName = (name: string): string =>
    name
        .split(' ')
        .map((word) => {
            if (word.length <= SHORT_WORD) return word;
            const shouting = word === word.toUpperCase();
            const lower = word === word.toLowerCase();
            if (!shouting && !lower) return word;
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
